import React from 'react';

import { Link } from 'gatsby';

import { Button, styled } from '@mui/material';

import { BrochureDownloadItem } from '@mewa/types';
import { DownloadButton, SectionDivider, SmileyWrongIcon } from '@mewa/ui';

import { compareServiceSegments } from '../../../../libs/data-sorting';
import t from '../../../../libs/translate';
import {
  SERVICE_SEGMENT_IMAGE_HEIGHT,
  SERVICE_SEGMENT_IMAGE_WIDTH,
} from '../../../components/home/service-segment-images';
import Img from '../../../components/image';
import { ConversionStepWrapper, FormProps, Header1 } from '../base';
import { GridContainer } from '../brochures-download/base';

type FormCompleteStepProps = FormProps & {
  brochures: BrochureDownloadItem[];
  langPrefix: string;
  selectedServiceSegment: string;
  submitWasSuccessful: boolean;
};

export const FormCompleteStep = ({
  activeStep,
  translations,
  brochures,
  langPrefix,
  selectedServiceSegment,
  submitWasSuccessful,
}: FormCompleteStepProps) => {
  const downloadText = t(translations, 'general_download_brochure');

  const isBrochureDownload = brochures.length > 0;

  return submitWasSuccessful ? (
    <StyledConversionStepWrapper
      isActive={activeStep === 1}
      className={isBrochureDownload ? 'brochures-download' : ''}
    >
      {isBrochureDownload ? (
        <>
          <Header1>
            {t(translations, 'brochures_download_form_complete_title')}
          </Header1>
          <FollowUpNote>
            {t(translations, 'brochures_download_follow_up_note')}
          </FollowUpNote>
          <BrochuresSections
            brochures={brochures}
            downloadText={downloadText}
          />
        </>
      ) : (
        <>
          <Header1>{t(translations, 'general_form_complete_title')}</Header1>
          <FollowUpNote>
            {t(translations, 'general_form_complete_follow_up_note', {
              serviceSegment: selectedServiceSegment,
            })}
          </FollowUpNote>
        </>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: isBrochureDownload ? 'flex-end' : undefined,
        }}
      >
        <Button
          component={Link}
          to={langPrefix || '/'}
          variant="contained"
          color="primary"
          sx={{
            marginTop: 'var(--spacer-xxxl)',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {t(translations, 'general_link_to_home_text')}
        </Button>
      </div>
    </StyledConversionStepWrapper>
  ) : (
    <StyledConversionStepWrapper isActive={activeStep === 1}>
      <Header1 style={{ marginBottom: '20px' }}>
        {t(translations, 'general_form_complete_error_title')}
      </Header1>
      <SmileyWrongIcon
        style={{
          width: '100px',
          height: '100px',
          marginLeft: '-16px',
        }}
      />
      <FollowUpNote>
        {t(translations, 'general_form_complete_error_text')}
      </FollowUpNote>
      <Button
        onClick={() => {
          window.location.reload();
        }}
        variant="text"
        color="secondary"
        sx={{
          marginTop: {
            xs: '32px',
            md: '46px',
          },
        }}
      >
        {t(translations, 'general_form_complete_error_button_label')}
      </Button>
    </StyledConversionStepWrapper>
  );
};

const StyledConversionStepWrapper = styled(ConversionStepWrapper)(
  ({ theme }) => ({
    [theme.breakpoints.up('mdSm')]: {
      paddingRight: '20%',

      '&.brochures-download': {
        paddingRight: 0,
      },
    },
  }),
);

const BrochuresSections = ({
  brochures,
  downloadText,
}: {
  brochures: BrochureDownloadItem[];
  downloadText: string;
}) => {
  const sections = brochures
    .filter((b) => b.files.length > 0)
    .sort((b1, b2) => compareServiceSegments(b1.key, b2.key));

  return sections.map((brochureSection, sectionIdx) => (
    <React.Fragment key={sectionIdx}>
      <SectionDivider
        title={brochureSection.label}
        count={brochureSection.files.length}
        sx={{ marginTop: 'var(--spacer-xxxl)' }}
      />
      <GridContainer>
        {brochureSection.files.map((file, i) => (
          <DownloadItem
            key={`${brochureSection.label}-${file.url}-${i}`}
            brochure={file}
            downloadText={downloadText}
            label={brochureSection.label}
          ></DownloadItem>
        ))}
      </GridContainer>
    </React.Fragment>
  ));
};

const DownloadItem = ({
  brochure,
  downloadText,
  label,
}: {
  brochure: BrochureDownloadItem['files'][0];
  downloadText: string;
  label: string;
}) => {
  const extension = brochure.extension.toUpperCase();
  const fileSize = (brochure.sizeInKiloBytes / 1000).toFixed(1) + ' MB';

  return (
    <DownloadContainer
      href={brochure.url}
      download={label}
      target="_blank"
      className="link-container"
    >
      <Img
        images={[
          {
            src: brochure.thumbnail,
            width: SERVICE_SEGMENT_IMAGE_WIDTH,
            height: SERVICE_SEGMENT_IMAGE_HEIGHT,
            media: '',
          },
        ]}
        alt=""
      ></Img>
      <BrochureName>{brochure.name}</BrochureName>
      <FileMeta>
        {extension}, {fileSize}
      </FileMeta>
      <StyledDownloadButton
        url={brochure.url}
        label={downloadText}
        sx={{ marginTop: '4px' }}
        linkComponent={'span'}
      />
    </DownloadContainer>
  );
};

const BrochureName = styled('h3')(({ theme }) => ({
  ...theme.typography.h6,
  margin: '10px 0 5px',
}));

const FileMeta = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: '0 0 var(--spacer-xxs)',
}));

const DownloadContainer = styled('a')(({ theme }) => ({
  color: 'var(--color-dark-coal)',
  display: 'grid',
  gridTemplateRows: 'auto auto auto 1fr',
  gridTemplateColumns: '1fr',
  justifyContent: 'flex-start',

  '.gatsby-image-wrapper, img': {
    aspectRatio: '320 / 420',
  },

  [theme.breakpoints.up('sm')]: {
    padding: '10px',
    margin: '-10px',

    ':hover': {
      boxShadow: '0px 0px 24px 4px rgba(211, 211, 211)',
    },
  },
}));

const FollowUpNote = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  marginBottom: 0,
  marginTop: 0,
  width: '90%',
}));

const StyledDownloadButton = styled(DownloadButton)({
  alignSelf: 'flex-end',
  justifySelf: 'flex-start',
});
